import {
  Badge, Box, Button, Flex, Input, Select,
  Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr,
} from "@chakra-ui/react";
import { useState, useReducer, useEffect } from "react";
import { api } from "../../../services/api";
import { useAuth } from "../../../Hooks/useAuth";
import { EditCoupon } from "../EditCoupon";
import { FiPlus, FiX } from "react-icons/fi";
import swal from "sweetalert";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";

function FormField({ label, children }) {
  return (
    <Box>
      <Text fontSize="11px" fontWeight="700" color="gray.500" mb="4px" textTransform="uppercase" letterSpacing="0.05em">{label}</Text>
      {children}
    </Box>
  );
}

const inputProps = {
  borderRadius: "8px",
  fontSize: "13px",
  bg: "white",
  border: "1px solid rgba(0,74,51,.2)",
  _focus: { borderColor: GREEN, boxShadow: `0 0 0 1px ${GREEN}` },
};

export function CouponCard() {
  const { user } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [coupon, setCoupon] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    { redemption_limit: "", valid_until: "", kind: "", code: "", discount: "" }
  );
  const [showForm, setShowForm] = useState(false);

  async function handleFetchCoupons() {
    try {
      const response = await api.get("/api/v1/admin/coupons", {
        headers: { "access-token": user?.token, client: user?.client, uid: user?.uid, accept: "application/json" },
      });
      setCoupons(response.data.coupons);
    } catch (error) {
      console.error("Error fetching coupons:", error);
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCoupon({ [name]: value });
  };

  async function handleCreate(e) {
    e.preventDefault();
    if (!coupon.redemption_limit || !coupon.valid_until || !coupon.kind || !coupon.code || !coupon.discount) {
      swal({ title: "Atenção", text: "Preencha todos os campos obrigatórios.", icon: "info" });
      return;
    }
    const formData = new FormData();
    formData.append("coupon[redemption_limit]", coupon.redemption_limit);
    formData.append("coupon[valid_until]", coupon.valid_until);
    formData.append("coupon[kind]", coupon.kind);
    formData.append("coupon[code]", coupon.code);
    formData.append("coupon[discount]", coupon.discount);
    try {
      const response = await api.post("/api/v1/admin/coupons", formData, {
        headers: { "access-token": user.token, client: user.client, uid: user.uid, accept: "application/json" },
      });
      if (response.status === 200) {
        swal({ title: "Criado!", text: "Cupom criado com sucesso.", icon: "success" });
        handleFetchCoupons();
        setShowForm(false);
        setCoupon({ redemption_limit: "", valid_until: "", kind: "", code: "", discount: "" });
      } else {
        swal({ title: "Erro", text: "Algo deu errado.", icon: "error" });
      }
    } catch (error) {
      swal({ title: "Erro", text: "Algo deu errado.", icon: "error" });
    }
  }

  async function toggleCouponActivity(e, couponId) {
    e.preventDefault();
    try {
      const response = await api.delete(`/api/v1/admin/coupons/${couponId}`);
      swal({ title: response.success ? "Desativado" : "Reativado", text: response.data?.message || "Status alterado.", icon: "success" });
      handleFetchCoupons();
    } catch (error) {
      swal({ title: "Erro", text: "Algo deu errado.", icon: "error" });
    }
  }

  useEffect(() => { handleFetchCoupons(); }, []);

  const fmtDate = (val) => {
    const d = new Date(val);
    if (isNaN(d)) return "—";
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }) + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Box>
      {/* Section header */}
      <Flex align="center" justify="space-between" mb="20px">
        <Box>
          <Text fontSize="11px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.08em">
            Promoções
          </Text>
          <Text fontSize="20px" fontWeight="900" color={GREEN}>
            Cupons de Desconto
            <Text as="span" fontSize="14px" fontWeight="600" color="gray.400" ml="8px">({coupons.length})</Text>
          </Text>
        </Box>
        <Button
          size="sm" borderRadius="8px" px="14px"
          bg={showForm ? "gray.100" : GREEN} color={showForm ? "gray.600" : ORANGE}
          fontWeight="700" fontSize="12px" _hover={{ opacity: 0.88 }}
          leftIcon={showForm ? <FiX size={13} /> : <FiPlus size={13} />}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "Cancelar" : "Novo cupom"}
        </Button>
      </Flex>

      {/* Create form */}
      {showForm && (
        <Box
          bg="white" borderRadius="14px" p="20px" mb="20px"
          border="1px solid rgba(0,74,51,.12)" boxShadow="0 2px 10px rgba(0,74,51,.06)"
        >
          <Text fontSize="12px" fontWeight="700" color="gray.500" mb="14px">Novo cupom</Text>
          <Flex gap="12px" flexWrap="wrap">
            <Box flex="2" minW="160px">
              <FormField label="Código">
                <Input name="code" value={coupon.code} onChange={handleChange} placeholder="ex: BOOZEN10" {...inputProps} />
              </FormField>
            </Box>
            <Box flex="2" minW="180px">
              <FormField label="Válido até">
                <Input name="valid_until" value={coupon.valid_until} type="datetime-local" onChange={handleChange} {...inputProps} />
              </FormField>
            </Box>
            <Box flex="1" minW="110px">
              <FormField label="Limite de uso">
                <Input name="redemption_limit" value={coupon.redemption_limit} type="number" placeholder="100" onChange={handleChange} {...inputProps} />
              </FormField>
            </Box>
          </Flex>
          <Flex gap="12px" mt="12px" flexWrap="wrap">
            <Box flex="2" minW="160px">
              <FormField label="Tipo de desconto">
                <Select name="kind" value={coupon.kind} onChange={handleChange} {...inputProps}>
                  <option value="">Selecione</option>
                  <option value="fixed">Fixo (R$)</option>
                  <option value="percentage">Porcentagem (%)</option>
                </Select>
              </FormField>
            </Box>
            <Box flex="1" minW="110px">
              <FormField label="Valor">
                <Input name="discount" value={coupon.discount} type="number" placeholder="0" onChange={handleChange} {...inputProps} />
              </FormField>
            </Box>
            <Flex align="flex-end" pb="0">
              <Button
                borderRadius="8px" px="20px" h="40px"
                bg={GREEN} color={ORANGE} fontWeight="700" fontSize="13px"
                _hover={{ opacity: 0.88 }}
                onClick={handleCreate}
              >
                Cadastrar cupom
              </Button>
            </Flex>
          </Flex>
        </Box>
      )}

      {/* Table */}
      <Box
        bg="white" borderRadius="16px" border="1px solid rgba(0,74,51,.08)"
        boxShadow="0 2px 12px rgba(0,74,51,.05)" overflow="hidden"
      >
        {coupons.length === 0 ? (
          <Box py="48px" textAlign="center">
            <Text fontSize="15px" fontWeight="700" color="gray.400">Nenhum cupom cadastrado</Text>
          </Box>
        ) : (
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead bg="rgba(0,74,51,.04)">
                <Tr>
                  <Th color={GREEN} fontSize="11px" py="12px">#</Th>
                  <Th color={GREEN} fontSize="11px">Código</Th>
                  <Th color={GREEN} fontSize="11px">Válido até</Th>
                  <Th color={GREEN} fontSize="11px">Limite</Th>
                  <Th color={GREEN} fontSize="11px">Restantes</Th>
                  <Th color={GREEN} fontSize="11px">Tipo</Th>
                  <Th color={GREEN} fontSize="11px">Desconto</Th>
                  <Th color={GREEN} fontSize="11px">Status</Th>
                  <Th color={GREEN} fontSize="11px">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {coupons.map(c => {
                  const isInactive = !!c.deactivated_at;
                  const isExpired = new Date(c.valid_until) < new Date();
                  const remaining = c.redemption_limit - (c.usage_count || 0);

                  return (
                    <Tr
                      key={c.id}
                      bg={isInactive ? "rgba(231,76,60,.04)" : "white"}
                      opacity={isInactive ? 0.75 : 1}
                      _hover={{ bg: isInactive ? "rgba(231,76,60,.07)" : "rgba(0,74,51,.025)" }}
                      transition="background 0.1s"
                    >
                      <Td py="10px">
                        <Text fontSize="12px" fontWeight="700" color={GREEN}>#{c.id}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="12px" fontWeight="800" color="gray.700" letterSpacing="0.04em">{c.code}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="11px" color={isExpired ? "red.400" : "gray.600"}>{fmtDate(c.valid_until)}</Text>
                        {isExpired && <Badge colorScheme="red" fontSize="9px" borderRadius="4px">Expirado</Badge>}
                      </Td>
                      <Td>
                        <Text fontSize="12px" color="gray.600">{c.redemption_limit}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="12px" fontWeight="700" color={remaining <= 0 ? "red.400" : "gray.700"}>{remaining}</Text>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={c.kind === "fixed" ? "blue" : "purple"}
                          fontSize="10px" borderRadius="6px" px="6px"
                        >
                          {c.kind === "fixed" ? "Fixo" : "Porcentagem"}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontSize="12px" fontWeight="700" color={GREEN}>
                          {c.kind === "fixed"
                            ? c.discount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
                            : `${c.discount}%`}
                        </Text>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={isInactive ? "red" : "green"}
                          fontSize="10px" borderRadius="6px" px="6px"
                        >
                          {isInactive ? "Inativo" : "Ativo"}
                        </Badge>
                      </Td>
                      <Td>
                        <Flex gap="6px">
                          <EditCoupon couponId={c.id} thisCoupon={c} handleFetchCoupons={handleFetchCoupons} />
                          <Button
                            size="xs" borderRadius="7px" px="10px" h="26px"
                            bg={isInactive ? "#27ae60" : "#e74c3c"} color="white"
                            fontWeight="700" fontSize="11px" _hover={{ opacity: 0.85 }}
                            onClick={e => toggleCouponActivity(e, c.id)}
                          >
                            {isInactive ? "Ativar" : "Desativar"}
                          </Button>
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
        <Box px="20px" py="10px" borderTop="1px solid rgba(0,74,51,.06)">
          <Text fontSize="12px" color="gray.400">
            {coupons.filter(c => !c.deactivated_at).length} ativo{coupons.filter(c => !c.deactivated_at).length !== 1 ? "s" : ""} de {coupons.length} cupom{coupons.length !== 1 ? "s" : ""}
          </Text>
        </Box>
      </Box>
    </Box>
  );
}
